import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotId, setForgotId] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { identifier, password });
      login(res.data.token, res.data.user, remember);
      toast.success("Uspješna prijava");
      navigate(res.data.user.role === "master" ? "/master" : "/klub");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { identifier: forgotId });
      toast.success(res.data.message);
      setForgotOpen(false);
      setForgotId("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="App min-h-screen bg-background">
      <Navbar />
      <div className="max-w-md mx-auto px-5 py-16">
        <div className="border-2 border-foreground bg-card p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Pristup panelu</div>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-6">Prijava</h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="identifier">Ime kluba ili email</Label>
              <Input
                id="identifier"
                className="rounded-none border-2 mt-1.5"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="npr. Plesni klub Podgorica"
                required
                data-testid="login-identifier"
              />
            </div>
            <div>
              <Label htmlFor="password">Lozinka</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  className="rounded-none border-2 pr-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  title={showPw ? "Sakrij lozinku" : "Prikaži lozinku"}
                  data-testid="toggle-login-password"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="rounded-none" data-testid="remember-me" />
                Zapamti me
              </label>
              <button type="button" onClick={() => setForgotOpen(true)} className="text-sm text-primary font-semibold hover:underline" data-testid="forgot-password-link">
                Zaboravljena lozinka?
              </button>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-none bg-primary hover:bg-primary/90 h-11" data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Prijavi se"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Nemate nalog?{" "}
            <Link to="/registracija" className="text-primary font-semibold hover:underline" data-testid="login-to-register">
              Registrujte klub
            </Link>
          </p>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="rounded-none border-2 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">Zaboravljena lozinka</DialogTitle>
            <DialogDescription>
              Unesite ime kluba ili email. Zahtjev za reset lozinke biće poslat masteru UPUS CG, koji će vam dodijeliti novu lozinku.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForgot} className="space-y-4">
            <div>
              <Label htmlFor="forgot-id">Ime kluba ili email</Label>
              <Input id="forgot-id" className="rounded-none border-2 mt-1.5" value={forgotId} onChange={(e) => setForgotId(e.target.value)} required data-testid="forgot-identifier" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={forgotLoading} className="w-full rounded-none bg-primary hover:bg-primary/90 h-11" data-testid="forgot-submit">
                {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pošalji zahtjev"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
